import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Calendar, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
    
    // Handle OAuth callback
    const code = searchParams.get('code');
    if (code) {
      handleOAuthCallback(code, searchParams.get('state'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/booking/status`);
      if (response.data.configured) {
        setStatus('configured');
        setMessage(response.data.message);
      } else {
        setStatus('not_configured');
        setMessage(response.data.message);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to check booking system status');
    }
  };

  const handleAuthorize = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/booking/auth/url`);
      window.location.href = response.data.authorization_url;
    } catch (error) {
      setMessage('Failed to get authorization URL');
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async (code, state) => {
    setLoading(true);
    setStatus('processing');
    setMessage('Processing authorization...');

    try {
      const response = await axios.get(`${API}/booking/auth/callback`, {
        params: { code, state }
      });

      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Authorization failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_d70cc816-e4d3-4f6e-bfaa-57b96c01f1a7/artifacts/w1f3hxo7_LTV-Production-LOGO-%281%29-white-2.png" 
            alt="LTV Production" 
            className="h-8 w-auto cursor-pointer"
            style={{ filter: 'invert(1)' }}
            onClick={() => navigate('/')}
          />
        </div>
      </header>

      <div className="flex items-center justify-center min-h-[80vh] px-6">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-3xl">Booking System Setup</CardTitle>
            <CardDescription className="text-base">
              Configure Google Calendar integration for the booking system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === 'loading' && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
              </div>
            )}

            {status === 'not_configured' && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900">Authorization Required</p>
                    <p className="text-yellow-700 text-sm mt-1">
                      The booking system needs access to your Google Calendar to create bookings and generate Meet links.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-bold">What will be configured:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Access to your Google Calendar</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Automatic booking creation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Google Meet link generation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Automatic email notifications to clients</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={handleAuthorize}
                  disabled={loading}
                  className="w-full bg-black hover:bg-gray-800 text-white"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Calendar className="mr-2 h-5 w-5" />
                      Authorize Google Calendar
                    </>
                  )}
                </Button>
              </div>
            )}

            {status === 'processing' && (
              <div className="text-center py-8">
                <Loader2 className="h-12 w-12 animate-spin text-gray-600 mx-auto mb-4" />
                <p className="text-lg font-medium">{message}</p>
              </div>
            )}

            {status === 'configured' && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">All Set!</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-black hover:bg-gray-800 text-white"
                  size="lg"
                >
                  Go to Homepage
                </Button>
              </div>
            )}

            {status === 'success' && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Authorization Successful!</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <p className="text-sm text-gray-500">Redirecting to homepage...</p>
              </div>
            )}

            {status === 'error' && (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Authorization Failed</h3>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button
                  onClick={() => { setStatus('not_configured'); checkStatus(); }}
                  variant="outline"
                  className="border-2 border-black hover:bg-black hover:text-white"
                  size="lg"
                >
                  Try Again
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSetup;
